import { useLingui } from '@lingui/react/macro';
import { Check } from 'lucide-react';
import { z } from 'zod';

import { defineBlockComponent, type BlockComponentProps } from '@/shared/page';
import { readPath, resolvePatternText, textSchema } from '@/shared/patterns';
import { cn } from '@/shared/ui/cn';

export interface StepperStep {
  readonly id: string;
  readonly label: string;
  /** Quando a etapa começou, já formatado para mostrar ("22/09 16:05"). */
  readonly since?: string | null | undefined;
}

/** `progress` em andamento; `done` terminou tudo (verde); `canceled` parou na etapa ativa (vermelho). */
export type StepperOutcome = 'progress' | 'done' | 'canceled';

export interface StatusStepperProps {
  readonly steps: readonly StepperStep[];
  /** Índice da etapa ativa; o módulo decide (a regra de Crédito é pelo último evento, 02 §3.1). */
  readonly active: number;
  readonly outcome?: StepperOutcome;
  /** iPhone: barras segmentadas + "Etapa 2 de 5 · Aprovações" (protótipo `m_stepper`). */
  readonly compact?: boolean;
  readonly label?: string;
  readonly className?: string;
}

type StepState = 'done' | 'current' | 'pending';

function stateOf(index: number, active: number, outcome: StepperOutcome): StepState {
  if (outcome === 'done' || index < active) return 'done';
  return index === active ? 'current' : 'pending';
}

/**
 * Stepper de status do cabeçalho (P20): as etapas da esteira com a atual marcada. Burro de
 * propósito: recebe as etapas e o índice ativo. Web horizontal; iPhone em barras.
 */
export function StatusStepper({ steps, active, outcome = 'progress', compact = false, label, className }: StatusStepperProps) {
  const { t } = useLingui();
  const name = label ?? t`Andamento`;
  const stateText: Readonly<Record<StepState, string>> = { done: t`concluída`, current: t`atual`, pending: t`não iniciada` };
  const current = steps[Math.min(Math.max(active, 0), steps.length - 1)];

  if (compact) {
    const position = Math.min(active + 1, steps.length);
    return (
      <div data-slot="status-stepper" className={cn('flex flex-col gap-1.5', className)}>
        <ol aria-label={name} className="flex gap-1">
          {steps.map((step, index) => {
            const state = stateOf(index, active, outcome);
            return (
              <li key={step.id} className="flex-1" {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}>
                <span
                  aria-hidden
                  className={cn(
                    'block h-1 rounded-full',
                    state === 'pending' ? 'bg-input' : outcome === 'done' ? 'bg-ok' : outcome === 'canceled' && state === 'current' ? 'bg-err' : 'bg-primary',
                  )}
                />
                <span className="sr-only">
                  {step.label}: {stateText[state]}
                </span>
              </li>
            );
          })}
        </ol>
        {current ? (
          <div className="flex justify-between gap-3 text-xs text-muted-foreground">
            <span>
              {t`Etapa ${position} de ${steps.length}`} · <b className="font-semibold text-foreground">{current.label}</b>
            </span>
            {current.since ? <span className="tabular-nums">{t`desde ${current.since}`}</span> : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <ol aria-label={name} data-slot="status-stepper" className={cn('flex items-center rounded-card border border-border bg-card px-4.5 py-3.5', className)}>
      {steps.map((step, index) => {
        const state = stateOf(index, active, outcome);
        const last = index === steps.length - 1;
        const failed = outcome === 'canceled' && state === 'current';
        const filled = state === 'done' && (outcome === 'done' || index + 1 <= active);
        return (
          <li key={step.id} className={cn('flex min-w-0 items-center', last ? 'flex-none' : 'flex-1')} {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}>
            <div className="flex shrink-0 items-center gap-2">
              {state === 'done' ? (
                <span aria-hidden className={cn('inline-flex size-[22px] items-center justify-center rounded-full text-primary-foreground', outcome === 'done' ? 'bg-ok' : 'bg-primary')}>
                  <Check className="size-3.5" />
                </span>
              ) : state === 'current' ? (
                <span aria-hidden className={cn('inline-flex size-[22px] items-center justify-center rounded-full border-2 bg-card', failed ? 'border-err' : 'border-primary')}>
                  <span className={cn('size-2 rounded-full', failed ? 'bg-err' : 'bg-primary')} />
                </span>
              ) : (
                <span aria-hidden className="size-[22px] rounded-full border-2 border-input bg-card" />
              )}
              <div className="flex flex-col">
                <span className={cn('text-sm font-medium whitespace-nowrap', state === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>
                  {step.label}
                  <span className="sr-only">: {stateText[state]}</span>
                </span>
                {step.since ? <span className="text-xs text-muted-foreground tabular-nums">{step.since}</span> : null}
              </div>
            </div>
            {last ? null : <span aria-hidden className={cn('mx-2.5 h-0.5 min-w-4 flex-1', outcome === 'done' ? 'bg-ok' : filled ? 'bg-primary' : 'bg-border')} />}
          </li>
        );
      })}
    </ol>
  );
}

// ─── genérico de bloco ──────────────────────────────────────────────────────

/** Etapas por status: a ativa é a que lista o valor do campo (a regra simples; Crédito usa a própria). */
export const statusStepperBlockProps = z.object({
  field: z.string().min(1),
  steps: z.array(z.object({ id: z.string().min(1), label: textSchema, statuses: z.array(z.string()) })).min(2).max(8),
  doneStatuses: z.array(z.string()).optional(),
  canceledStatuses: z.array(z.string()).optional(),
  label: textSchema.optional(),
});
export type StatusStepperBlockProps = z.infer<typeof statusStepperBlockProps>;

/** Índice ativo e desfecho para o valor de status do dado. */
export function stepperStateFrom(data: unknown, props: StatusStepperBlockProps): { readonly active: number; readonly outcome: StepperOutcome } {
  const value = readPath(data, props.field);
  const status = typeof value === 'string' ? value : '';
  const active = props.steps.findIndex((step) => step.statuses.includes(status));
  const outcome: StepperOutcome = props.doneStatuses?.includes(status) ? 'done' : props.canceledStatuses?.includes(status) ? 'canceled' : 'progress';
  return { active: Math.max(active, 0), outcome };
}

type StepperView = BlockComponentProps<StatusStepperBlockProps, object>;

function useSteps(props: StatusStepperBlockProps) {
  const { i18n } = useLingui();
  return {
    steps: props.steps.map((step) => ({ id: step.id, label: resolvePatternText(i18n, step.label) })),
    label: props.label ? resolvePatternText(i18n, props.label) : undefined,
  };
}

function StatusStepperBlockWeb({ props, data }: StepperView) {
  const { steps, label } = useSteps(props);
  const { active, outcome } = stepperStateFrom(data, props);
  return <StatusStepper steps={steps} active={active} outcome={outcome} {...(label ? { label } : {})} />;
}

function StatusStepperBlockMobile({ props, data }: StepperView) {
  const { steps, label } = useSteps(props);
  const { active, outcome } = stepperStateFrom(data, props);
  return <StatusStepper compact steps={steps} active={active} outcome={outcome} {...(label ? { label } : {})} />;
}

/** `StatusStepper` como genérico do catálogo, sobre uma fonte `record` com o status. */
export const StatusStepperBlock = defineBlockComponent<StatusStepperBlockProps, object>({
  id: 'StatusStepper',
  description: 'Horizontal progress of workflow steps, the active one chosen by the record status; segmented bars on iPhone.',
  props: statusStepperBlockProps,
  render: { web: StatusStepperBlockWeb, mobile: StatusStepperBlockMobile },
});
